import { useTranslation } from '@input-web/configs/locale/locale.config';
import { Outlet } from '@tanstack/react-router';
import Button from '@ui/components/atoms/button/button.atom';
import Icon from '@ui/components/atoms/icon/icon.atom';
import Typography from '@ui/components/atoms/typography/typography.atom';
import Dialog from '@ui/components/molecules/dialog/dialog.molecule';
import SuspenseLoading from '@ui/components/molecules/suspense-loading/suspense-loading.molecule';
import Select from '@ui/components/organisms/select/select.organism';
import DashboardLayout from '@ui/components/templates/dashboard-layout/dashboard-layout.template';
import { clsxm } from '@ui/utils/style.util';
import { useMemo } from 'react';
import useStandardLayoutViewModel from './standard-layout.view-model';

export default function StandardLayoutView() {
  // #region Dependencies
  const { t } = useTranslation();
  // #endregion

  // #region View Model
  const { dialog, filter, menu, navbar, standardSearchState } = useStandardLayoutViewModel();
  const { handleNavigate } = standardSearchState;
  // #endregion

  // #region Computed Values
  const additionalSidebarContent = useMemo(
    () => (
      <section className="space-y-2.5 mt-4">
        {navbar.isDetailLayoutMatched && (
          <Button.Text
            className="flex items-center gap-3 text-black-charcoal"
            onClick={navbar.props.onActionClick}
          >
            <Icon.ArrowLeft className="w-4 h-4" />
            {t('standard.layout.sidebar.back')}
          </Button.Text>
        )}
        <Select
          className="border-none"
          disabled={navbar.isDetailLayoutMatched}
          label={t('standard.layout.sidebar.filter.label.subject')}
          onChange={(value) => {
            if (!Array.isArray(value)) handleNavigate({ subjectId: Number(value) });
          }}
          options={filter.subjectOptions}
          value={standardSearchState.state?.subjectId}
        />
        <Select
          className="border-none"
          disabled={navbar.isDetailLayoutMatched}
          label={t('standard.layout.sidebar.filter.label.university')}
          onChange={(value) => {
            if (Array.isArray(value)) return;
            handleNavigate({
              bunriId: undefined,
              facultyId: undefined,
              universityId: value.toString(),
            });
          }}
          options={filter.universityOptions}
          value={standardSearchState.state?.universityId}
        />
        <Select
          className="border-none"
          disabled={navbar.isDetailLayoutMatched}
          label={t('standard.layout.sidebar.filter.label.bunri')}
          onChange={(value) => {
            if (Array.isArray(value)) return;
            handleNavigate(
              filter.bunriOptions.length
                ? { bunriId: Number(value) }
                : { facultyId: Number(value) },
            );
          }}
          options={filter.bunriOptions.length ? filter.bunriOptions : filter.facultyOptions}
          value={
            filter.bunriOptions.length
              ? standardSearchState.state?.bunriId
              : standardSearchState.state?.facultyId
          }
        />
        {navbar.isDetailLayoutMatched && (
          <div className="space-y-2.5">
            <Typography.BodyOne>{t('standard.layout.sidebar.part-list')}</Typography.BodyOne>
            <nav>
              <ul className="space-y-2.5">
                {menu.items.map((item) => (
                  <li
                    key={`sidebar-standard-list-item-${item.key}`}
                    className={clsxm(
                      'px-2 py-1 text-base hover:bg-white-yellow transition-colors rounded-lg cursor-pointer',
                      item.active && 'bg-white-yellow',
                    )}
                  >
                    <button
                      className="w-full text-left"
                      onClick={() => {
                        if (!item.active) item.onClick();
                      }}
                      type="button"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        )}
      </section>
    ),
    [
      navbar.isDetailLayoutMatched,
      t,
      filter.subjectOptions,
      filter.universityOptions,
      filter.bunriOptions,
      filter.facultyOptions,
      standardSearchState.state?.subjectId,
      standardSearchState.state?.universityId,
      standardSearchState.state?.bunriId,
      standardSearchState.state?.facultyId,
      menu.items,
      handleNavigate,
      navbar.props.onActionClick,
    ],
  );
  // #endregion

  return (
    <DashboardLayout
      additionalSidebarContent={additionalSidebarContent}
      contentClassName="px-0"
      navbar={navbar.props}
    >
      <Dialog {...dialog} />
      <SuspenseLoading>
        <Outlet />
      </SuspenseLoading>
    </DashboardLayout>
  );
}
